



































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ApplicationForm21Expenses from './application21-expenses.vue';
import ApplicationForm21Indicators from './application21-indicators.vue';
import {
    App21View,
    IApp21BpCheck,
    IApp21ExpenseRow,
    IApp21ExpensesData,
    IApp21IndicatorsData,
    IApp21KrRow,
    IApp21PprIndicatorRow,
    IApp21PprItem,
    IApp21PreviewBpResult,
    IApp21Reason,
    IApp21SaveError,
} from './app21InputForm';
import {
    App21Table,
    collectMissingReasons,
    collectMissingReasonsForPprs,
    hasAnyMissingReason,
    IMissingReason,
    INDICATOR_PRECISION,
    IReasonOption,
    isReasonMissing,
    MONEY_PRECISION,
    normalizeBipCode,
    reasonKey,
} from './app21Utils';
import { App21InputFormFacade } from './App21Facade';

// элемент списка подпрограмм для multiselect; key — индекс в pprList
interface IPprOption {
    value: string;
    key: number;
}

const I18N_PREFIX = 'modules.budget_execution_monitoring.reports.app21';

// таймаут «Обновить данные БП»: с запасом над обычным временем синхронизации
const UPDATE_BP_TIMEOUT_MS = 120000;

// отчёты кнопки «Скачать»: эндпоинт и ключ i18n префикса имени файла
type App21Report = 'app21' | 'app22';
const REPORTS: Record<App21Report, { url: string; fileNameKey: string }> = {
    app21: { url: '/api-py/app-21/report/download', fileNameKey: 'report_file_name' },
    app22: { url: '/api-py/app-21/report-22/download', fileNameKey: 'report22_file_name' },
};
// подпрограмма целевых трансфертов: при её наличии доступно «Приложение 22» (RQ.eAk.MONF05.02)
const TRANSFER_PPR = 11;

// код ЕБК дополняется нулями слева до 3 знаков: 1 → 001
const pad3 = (code: number | null | undefined): string => String(code ?? 0).padStart(3, '0');

@Component({
    components: {
        'app21-expenses': ApplicationForm21Expenses,
        'app21-indicators': ApplicationForm21Indicators,
    }
})
export default class ApplicationForm21 extends Vue {
    @Prop({ required: true }) private currForm!: any;
    // права на редактирование формы (статус, роль) — вычисляет BEIFOpenWindow
    @Prop({ default: false }) private editAccess!: boolean;

    // текущее представление формы: «Расходы по БП» или «Показатели»
    private view: App21View = 'expenses';
    private readonly i18nPrefix = I18N_PREFIX;

    // проверка актуальности данных БП (/api-py/app-21/bp-check): расхождения отдельно по КР и по ППР (доработка 2)
    private hasKrChanges = false;
    private hasPprChanges = false;
    // нет утверждённой версии БП (АС5) — баннер NTF.eAk.MONF01.04 / 05
    private noBpVersion = false;
    // баннер закрыт крестиком — своя вкладка; скрыт до следующего «Сохранить», обновления БП или открытия формы (п. 2.5)
    private krBannerClosed = false;
    private pprBannerClosed = false;

    // данные обеих вкладок живут здесь: не теряются при переключении вкладок
    // и доступны для проверки причин при сохранении
    private prgNameRu: string | null = null;
    private prgNameKk: string | null = null;
    // строки budget_execution_application21 по программе и всем ППР — общие для обеих вкладок;
    // у БП без подпрограмм строка ppr = null показывается на обеих вкладках одним объектом
    private expenses: IApp21ExpenseRow[] = [];
    // строки budget_execution_application21_kr — раздел 2 «Расходов»
    private kr: IApp21KrRow[] = [];
    // подпрограммы БП (по возрастанию кода) и индекс выбранной
    private pprList: IApp21PprItem[] = [];
    private selectedPprIdx = 0;
    // строки budget_execution_application21_ppr по всем ППР — раздел 2 «Показателей»
    private indicators: IApp21PprIndicatorRow[] = [];
    // причины из dict_reasons_apps для app21 — один справочник на обе вкладки
    private reasonsDict: IApp21Reason[] = [];
    private loading = false;
    // строки, у которых при последнем неудачном сохранении не хватало причины (ключ — reasonKey);
    // подсвечиваются только они и только пока ошибка не исправлена
    private highlighted: Record<string, boolean> = {};
    // идёт сохранение — повторное нажатие «Сохранить» игнорируется
    private saving = false;
    // значения строк на момент последней загрузки из БД (ключ — reasonKey): по ним определяются
    // несохранённые изменения; fact — только у КР/ППР (факт расходов не редактируется)
    // фасад хранится, чтобы отдавать кнопке флаг «идёт обновление БП»
    private facade: App21InputFormFacade | null = null;
    private bpUpdating = false;
    private savedSnapshot: Record<string, { fact?: number | null; reason_id: number | null }> = {};
    // несохранённое «Обновить данные БП»: 
    // показанная версия БП и отпечаток формы для «Сохранить»;
    // null — данные БП в форме совпадают с сохранёнными
    private bpPreview: { variant: string; formToken: string } | null = null;


    private mounted() {
        this.registerFacade();
        this.loadData(false, true);
    }

    private registerFacade() {
        const facade = new App21InputFormFacade();
        facade.saveData = this.saveData.bind(this);
        facade.updateBp = this.updateBp.bind(this);
        facade.onBackFn = this.handleBack.bind(this);
        facade.downloadList = this.buildDownloadList();
        this.facade = facade;
        this.$emit('onRegisterFacade', facade);
    }

    // пункты «Скачать»: «Приложение 22» — только при строке расходов с ppr = 11 (RQ.eAk.MONF05.02);
    private buildDownloadList() {
        const vm = this;
        const item = (report: App21Report, nameKey: string, idx: number) => ({
            get name() { return vm.$t(`${I18N_PREFIX}.${nameKey}`) as string; },
            fn: this.downloadReport.bind(this),
            args: [report],
            idx,
        });
        const list = [item('app21', 'report_xlsx', 1)];
        if (this.hasTransferPpr) list.push(item('app22', 'report22_xlsx', 2));
        return list;
    }

    private get hasTransferPpr(): boolean {
        return this.expenses.some(r => r.ppr === TRANSFER_PPR);
    }

    // строки расходов загрузились или сменилась форма — пересобираем пункты «Скачать»
    @Watch('hasTransferPpr')
    private onTransferPprChanged() {
        if (this.facade) this.facade.downloadList = this.buildDownloadList();
    }

    // несохранённые изменения или отклонение без причины — предупреждаем.
    // Проверяются те же строки, что сохраняются; 
    public async handleBack(): Promise<boolean> {
        if (!this.editAccess) return true;
        const hasMissing = collectMissingReasons(this.expenses, this.kr, this.indicators, this.selectedPpr).length > 0;
        if (!this.isDirty && !hasMissing) return true;

        // закрывается только кнопками «Остаться» / «Выйти»
        const confirmed = await this.$bvModal.msgBoxConfirm(
            this.$t(`${I18N_PREFIX}.unsaved_warning`) as string,
            {
                title: this.$t('modules.common.attention') as string,
                okVariant: 'danger',
                okTitle: this.$t(`${I18N_PREFIX}.exit`) as string,
                cancelTitle: this.$t(`${I18N_PREFIX}.stay`) as string,
                centered: true,
                noCloseOnBackdrop: true,
                noCloseOnEsc: true,
                hideHeaderClose: true,
            }
        );
        return confirmed === true;
    }

    // «Обновить данные БП»: данные последней утверждённой версии БП показываются в форме без записи в БД
    // (п. 1.1); факты и причины — сохранённые в БД (п. 1.3). Запись — по «Сохранить» (п. 1.7).
    // ФЛК и bp-check не вызываются: они пишут в БД. Отклонения, % и обязательность причин пересчитываются на фронте
    public async updateBp(): Promise<void> {
        if (this.bpUpdating || !this.currForm) return;
        if (this.isDirty) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                this.$t(`${I18N_PREFIX}.warn_unsaved_update_bp`) as string,
                {
                    title: this.$t('modules.common.attention') as string,
                    okVariant: 'success',
                    okTitle: this.$t('modules.budget_execution_monitoring.input_forms.massage_tools.yes') as string,
                    cancelTitle: this.$t('modules.budget_execution_monitoring.input_forms.massage_tools.cancellation') as string,
                    centered: true,
                    noCloseOnBackdrop: true,
                    noCloseOnEsc: true,
                    hideHeaderClose: true,
                }
            );
            if (confirmed !== true) return;
        }
        this.setBpUpdating(true);
        // зависший запрос прерывается и обрабатывается как ошибка обновления (АС4)
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), UPDATE_BP_TIMEOUT_MS);
        try {
            const resp = await fetch('/api-py/app-21/data/preview-bp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ form_id: this.currForm.id }),
                signal: controller.signal,
            });
            if (!resp.ok) {
                throw new Error(`${resp.status} ${resp.statusText}`);
            }
            const result: IApp21PreviewBpResult = await resp.json();
            if (!result.success) {
                // АС5: версии БП нет — форма не меняется
                if (result.no_bp_version) {
                    this.noBpVersion = true;
                    this.view = 'expenses';
                    return;
                }
                console.warn('app21 preview-bp: данные не получены', result);
                return;
            }
            // данные версии — в форму; снимок по тем же строкам: факты и причины в них — сохранённые в БД
            this.expenses = result.expenses ?? [];
            this.kr = result.kr ?? [];
            this.indicators = result.indicators ?? [];
            this.highlighted = {};
            this.buildSnapshot();
            // данные версии совпали с сохранёнными — предупреждений о несохранённом нет (п. 1.2)
            this.bpPreview = result.has_bp_changes && result.variant && result.form_token
                ? { variant: result.variant, formToken: result.form_token }
                : null;
            // баннеры скрываются, открывается «Расходы по БП» (сценарий, шаг 5)
            this.resetBpBanners();
            this.noBpVersion = false;
            this.view = 'expenses';
            // в версии нет данных по БП: показатели будут удалены при сохранении (п. 1.4, сообщение 1)
            if (result.empty_bp) {
                this.notifyEmptyBp(!!result.had_bp_data);
            }
        } catch (error) {
            // АС4 / NTF.eAk.MONF01.03: данные формы (и несохранённые правки) не меняются,
            // пользователь продолжает работу с текущими, на вкладке «Расходы»
            console.error(error);
            this.view = 'expenses';
            this.makeToast(
                'danger',
                this.$t(`${I18N_PREFIX}.attention`) as string,
                this.$t(`${I18N_PREFIX}.update_bp_error`) as string, 5000);
        } finally {
            clearTimeout(timer);
            this.setBpUpdating(false);
        }
    }

    // отчёт «Приложение 21» или «Приложение 22» по текущей форме
    public async downloadReport(report: App21Report): Promise<void> {
        const { url: reportUrl, fileNameKey } = REPORTS[report];
        try {
            const resp = await fetch(reportUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    form_id: this.currForm.id,
                    prg: this.currForm.prg,
                    locale: this.$i18n.locale,
                }),
            });
            if (!resp.ok) {
                throw new Error(`${resp.status} ${resp.statusText}`);
            }
            const url = URL.createObjectURL(await resp.blob());
            const a = document.createElement('a');
            a.href = url;
            const prefix = this.$t(`${I18N_PREFIX}.${fileNameKey}`) as string;
            a.download = `${prefix}_${this.currForm.year}_${pad3(this.currForm.abp)}_${pad3(this.currForm.prg)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            this.makeToast(
                'danger',
                this.$t(`${I18N_PREFIX}.attention`) as string,
                this.$t(`${I18N_PREFIX}.report_error`) as string,
                5000
            );
        }
    }

    private setBpUpdating(value: boolean) {
        this.bpUpdating = value;
        if (this.facade) this.facade.bpUpdating = value;
    }

    // «Сохранить»: при показателях с отклонением без причины сохранение блокируется (NTF.eAk.MONF01.02).
    // Сохраняются «Расходы» (программа в целом и КР) и выбранная подпрограмма на «Показателях»
    public async saveData(): Promise<void> {
        await this.persistData();
    }

    // сохранение для «Сохранить» и автосохранения при смене подпрограммы; true — данные сохранены
    private async persistData(): Promise<boolean> {
        // закрытые крестиком баннеры возвращаются при любом сохранении, пока данные БП не обновлены (п. 2.5);
        // расхождения — по последней проверке, после успешного сохранения — по новой
        this.krBannerClosed = false;
        this.pprBannerClosed = false;
        if (this.saving) return false;
        // после «Обновить данные БП» проверяются и подпрограммы, где пользователь менял факт или причину (п. 1.9)
        const missing = this.bpPreview
            ? collectMissingReasonsForPprs(this.expenses, this.kr, this.indicators, this.pprList, this.selectedPpr, this.changedPprs)
            : collectMissingReasons(this.expenses, this.kr, this.indicators, this.selectedPpr);
        if (missing.length) {
            await this.showMissingReasons(missing);
            return false;
        }

        this.saving = true;
        try {
            const resp = await fetch('/api-py/app-21/data/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(this.bpPreview ? this.buildApplyBpPayload(this.bpPreview) : this.buildSavePayload()),
            });
            // форму изменили после обновления или версия БП больше не действует — нужно обновить заново
            if (resp.status === 409) {
                this.makeToast(
                    'danger',
                    this.$t(`${I18N_PREFIX}.attention`) as string,
                    this.$t(`${I18N_PREFIX}.form_changed`) as string,
                    7000
                );
                return false;
            }
            if (!resp.ok) {
                throw new Error(`${resp.status} ${resp.statusText}`);
            }
            const json: { success: boolean; errors?: IApp21SaveError[] } = await resp.json();
            if (!json.success) {
                // сервер нашёл пропуски, которых не увидел фронт
                await this.showMissingReasons(this.toMissingReasons(json.errors ?? []));
                return false;
            }
            this.makeToast(
                'success',
                this.$t('modules.common.saving') as string,
                this.$t(`${I18N_PREFIX}.saved`) as string
            );
            // данные БП записаны; выбранная подпрограмма остаётся той же после перезагрузки
            this.bpPreview = null;
            await this.loadData(true);
            return true;
        } catch (error) {
            this.makeToast(
                'danger',
                this.$t('modules.budget_execution_monitoring.reports.app_9.save_error') as string,
                this.$t(`${I18N_PREFIX}.save_error`) as string
            );
            return false;
        } finally {
            this.saving = false;
        }
    }

    // строки для сохранения: только reason_id для budget_execution_application21, fact и reason_id для КР/ППР
    private buildSavePayload() {
        const rows = this.scopeRows;
        return {
            form_id: this.currForm.id,
            ppr: this.selectedPpr ? this.selectedPpr.ppr : null,
            expenses: rows.expenses.map(r => ({ id: r.id, reason_id: r.reason_id })),
            kr: rows.kr.map(r => ({ id: r.id, fact: r.fact, reason_id: r.reason_id })),
            indicators: rows.indicators.map(r => ({ id: r.id, fact: r.fact, reason_id: r.reason_id })),
        };
    }

    // «Сохранить» после «Обновить данные БП» (п. 1.7, 1.10): показанная версия и отпечаток формы;
    // изменённые строки расходов и ППР всех подпрограмм (автосохранения при переключении нет) и все КР.
    // id новых КР/ППР из предпросмотра на сервере недействительны — КР по kr_code, ППР по (ppr, indicator_id, bip_code)
    private buildApplyBpPayload(preview: { variant: string; formToken: string }) {
        return {
            form_id: this.currForm.id,
            ppr: this.selectedPpr ? this.selectedPpr.ppr : null,
            apply_bp: true,
            bp_variant: preview.variant,
            form_token: preview.formToken,
            expenses: this.expenses
                .filter(r => this.isRowChanged('expenses', r.id, r.reason_id))
                .map(r => ({ id: r.id, reason_id: r.reason_id })),
            kr: this.kr.map(r => ({ kr_code: r.kr_code, fact: r.fact, reason_id: r.reason_id })),
            indicators: this.indicators
                .filter(r => this.isRowChanged('ppr', r.id, r.reason_id, r.fact))
                .map(r => ({
                    ppr: r.ppr,
                    indicator_id: r.indicator_id,
                    bip_code: normalizeBipCode(r.bip_code),
                    fact: r.fact,
                    reason_id: r.reason_id,
                })),
        };
    }

    // строки, которые сохраняются и проверяются при «Назад»: программа в целом и все КР,
    // выбранная подпрограмма и её ППР; другие подпрограммы не учитываются
    private get scopeRows(): { expenses: IApp21ExpenseRow[]; kr: IApp21KrRow[]; indicators: IApp21PprIndicatorRow[] } {
        const selected = this.selectedPpr;
        const inSelected = (ppr: number | null) => selected !== null && ppr === selected.ppr;
        return {
            expenses: this.expenses.filter(r => r.ppr === null || inSelected(r.ppr)),
            kr: this.kr,
            indicators: this.indicators.filter(r => inSelected(r.ppr)),
        };
    }

    // снимок значений после загрузки из БД
    private buildSnapshot() {
        const snapshot: Record<string, { fact?: number | null; reason_id: number | null }> = {};
        for (const r of this.expenses) {
            snapshot[reasonKey('expenses', r.id)] = { reason_id: r.reason_id };
        }
        for (const r of this.kr) {
            snapshot[reasonKey('kr', r.id)] = { fact: r.fact, reason_id: r.reason_id };
        }
        for (const r of this.indicators) {
            snapshot[reasonKey('ppr', r.id)] = { fact: r.fact, reason_id: r.reason_id };
        }
        this.savedSnapshot = snapshot;
    }

    // строка отличается от снимка последней загрузки; изменили и вернули прежнее — не изменение
    private isRowChanged(table: App21Table, id: number, reasonId: number | null, fact?: number | null): boolean {
        const saved = this.savedSnapshot[reasonKey(table, id)];
        return !saved || saved.reason_id !== reasonId || (fact !== undefined && saved.fact !== fact);
    }

    // подпрограммы, в которых пользователь менял факт или причину (строка расходов ППР или её ППР)
    private get changedPprs(): Set<number | null> {
        const pprs = new Set<number | null>();
        for (const r of this.expenses) {
            if (r.ppr !== null && this.isRowChanged('expenses', r.id, r.reason_id)) pprs.add(r.ppr);
        }
        for (const r of this.indicators) {
            if (this.isRowChanged('ppr', r.id, r.reason_id, r.fact)) pprs.add(r.ppr);
        }
        return pprs;
    }

    // несохранённые изменения: обновление БП, не записанное в БД (п. 1.2, 1.3), или правки фактов и причин.
    // Проверяются все подпрограммы: после обновления автосохранения при переключении нет (п. 1.6);
    // без обновления остальные подпрограммы уже сохранены автосохранением
    private get isDirty(): boolean {
        return this.bpPreview !== null
            || this.expenses.some(r => this.isRowChanged('expenses', r.id, r.reason_id))
            || this.kr.some(r => this.isRowChanged('kr', r.id, r.reason_id, r.fact))
            || this.indicators.some(r => this.isRowChanged('ppr', r.id, r.reason_id, r.fact));
    }

    // ошибки сервера → строка на экране и вкладка; после обновления БП КР и ППР приходят по ключам. Сначала «Расходы»
    private toMissingReasons(errors: IApp21SaveError[]): IMissingReason[] {
        const totalId = this.expenses.find(r => r.ppr === null)?.id;
        const missing: IMissingReason[] = [];
        for (const e of errors) {
            if (e.table === 'expenses' && e.id !== undefined) {
                const row = this.expenses.find(r => r.id === e.id);
                missing.push({
                    table: 'expenses',
                    id: e.id,
                    ppr: row?.ppr ?? null,
                    view: e.id === totalId ? 'expenses' : 'indicators',
                });
            } else if (e.table === 'kr') {
                const row = e.id !== undefined
                    ? this.kr.find(r => r.id === e.id)
                    : this.kr.find(r => r.kr_code === e.kr_code);
                if (row) missing.push({ table: 'kr', id: row.id, view: 'expenses' });
            } else if (e.table === 'ppr') {
                const row = e.id !== undefined
                    ? this.indicators.find(r => r.id === e.id)
                    : this.indicators.find(r => r.ppr === (e.ppr ?? null)
                        && r.indicator_id === e.indicator_id
                        && normalizeBipCode(r.bip_code) === normalizeBipCode(e.bip_code));
                if (row) missing.push({ table: 'ppr', id: row.id, ppr: row.ppr, view: 'indicators' });
            }
        }
        return missing.sort((a, b) => (a.view === b.view ? 0 : a.view === 'expenses' ? -1 : 1));
    }

    // подсветка строк с ошибкой, переход на первую вкладку с ошибкой и модальное окно
    private async showMissingReasons(missing: IMissingReason[]) {
        const highlighted: Record<string, boolean> = {};
        for (const m of missing) {
            highlighted[reasonKey(m.table, m.id)] = true;
        }
        this.highlighted = highlighted;
        // на текущей вкладке ошибок нет — переходим на первую вкладку с ошибкой
        if (missing.length && !missing.some(m => m.view === this.view)) {
            this.view = missing[0].view;
        }
        // на «Показателях» в выбранной подпрограмме ошибок нет — переходим к первой подпрограмме с ошибкой
        const selected = this.selectedPpr;
        const onIndicators = missing.filter(m => m.view === 'indicators');
        if (this.view === 'indicators' && onIndicators.length && !onIndicators.some(m => m.ppr === selected?.ppr)) {
            const idx = this.pprList.findIndex(p => p.ppr === onIndicators[0].ppr);
            if (idx >= 0) this.selectedPprIdx = idx;
        }
        await this.$bvModal.msgBoxOk(
            this.$t(`${I18N_PREFIX}.save_validation_error`) as string,
            { title: this.$t('modules.common.attention') as string, centered: true }
        );
    }

    private makeToast(
        variant: string, 
        title: string, 
        body: string, 
        autoHideDelay = 3000
    ) {
        this.$bvToast.toast(body, {
            title: title,
            variant,
            toaster: 'b-toaster-top-center',
            autoHideDelay,
        });
    }

    // другая форма — данные другие
    @Watch('currForm.id')
    private onFormChanged() {
        this.view = 'expenses';
        // баннеры предыдущей формы не должны висеть, пока грузится новая
        this.resetBpBanners();
        this.noBpVersion = false;
        this.loadData(false, true);
    }


    // обе вкладки грузятся сразу: при сохранении проверяются обе, даже если «Показатели» не открывались.
    // keepPpr — после сохранения остаться на той же подпрограмме;
    // notifyEmptyBp — открытие формы: сообщить, если при первичной загрузке в версии БП нет данных
    private async loadData(keepPpr = false, notifyEmptyBp = false) {
        if (!this.currForm) return;
        const prevPpr = keepPpr ? this.selectedPpr : null;
        this.loading = true;
        this.highlighted = {};
        // форма из БД — несохранённого обновления БП нет
        this.bpPreview = null;
        try {
            const [expensesData, indicatorsData, reasons] = await Promise.all([
                this.fetchView<IApp21ExpensesData>('expenses'),
                this.fetchView<IApp21IndicatorsData>('indicators'),
                this.fetchReasons(),
            ]);
            this.prgNameRu = expensesData.prg_name_ru ?? null;
            this.prgNameKk = expensesData.prg_name_kk ?? null;
            this.expenses = expensesData.expenses ?? [];
            this.kr = expensesData.kr ?? [];
            this.pprList = indicatorsData.ppr_list ?? [];
            this.indicators = indicatorsData.indicators ?? [];
            this.reasonsDict = reasons;
            this.buildSnapshot();
            // строки КР/ППР создаются на бэке в запросах выше — проверка строго после них
            await this.checkBp(notifyEmptyBp);
            // ФЛК — по данным из БД: loadData вызывается и после сохранения, и после обновления БП
            await this.updateFormFlk();
        } catch (error) {
            this.prgNameRu = null;
            this.prgNameKk = null;
            this.expenses = [];
            this.kr = [];
            this.pprList = [];
            this.indicators = [];
            this.savedSnapshot = {};
            this.hasKrChanges = false;
            this.hasPprChanges = false;
            this.noBpVersion = false;
        } finally {
            // по умолчанию — подпрограмма с наименьшим кодом (RQ.eAk.MONF01.33)
            const idx = prevPpr ? this.pprList.findIndex(x => x.ppr === prevPpr.ppr) : -1;
            this.selectedPprIdx = idx >= 0 ? idx : 0;
            this.loading = false;
        }
    }

    private async checkBp(notifyEmptyBp = false) {
        try {
            const resp = await fetch('/api-py/app-21/bp-check', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    form_id: this.currForm.id,
                    prg: this.currForm.prg,
                }),
            });
            if (!resp.ok) {
                throw new Error(`${resp.status} ${resp.statusText}`);
            }
            const result: IApp21BpCheck = await resp.json();
            this.hasKrChanges = !!result.kr_changed;
            this.hasPprChanges = !!result.ppr_changed;
            this.noBpVersion = !!result.no_bp_version;
            // только при открытии формы, не после сохранения и обновления БП
            if (notifyEmptyBp && result.empty_bp) {
                this.notifyEmptyBp(false);
            }
        } catch (error) {
            console.error(error);
            this.hasKrChanges = false;
            this.hasPprChanges = false;
            this.noBpVersion = false;
        }
    }

    // в утверждённой версии нет данных по БП; hadBpData — ранее загруженные показатели будут удалены после сохранения
    private notifyEmptyBp(hadBpData: boolean) {
        this.makeToast(
            'warning',
            this.$t(`${I18N_PREFIX}.attention`) as string,
            this.$t(`${I18N_PREFIX}.${hadBpData ? 'bp_empty_after_save' : 'bp_empty'}`) as string,
            5000
        ); 
    }
    
    // ФЛК формы для согласования (budget_execution_forms.flk): список форм не даёт отправить на согласование
    // форму с flk = null (не открывалась) или true (ошибки). Ошибка — нет данных 4-20 или отклонение без причины.
    // В режиме просмотра (статусы 31/26) флаг не меняем
    private async updateFormFlk() {
        if (!this.editAccess) return;
        const flk = this.expenses.length === 0 || hasAnyMissingReason(this.expenses, this.kr, this.indicators);
        try {
            await fetch('/api-py/input-forms/flk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ form_id: this.currForm.id, flk }),
            });
        } catch (error) {
            console.error(error);
        }
    }

    private async fetchView<T>(view: App21View): Promise<Partial<T>> {
        const resp = await fetch('/api-py/app-21/data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                view,
                form_id: this.currForm.id,
                prg: this.currForm.prg,
            }),
        });
        if (!resp.ok) {
            throw new Error(`${resp.status} ${resp.statusText}`);
        }
        return await resp.json();
    }

    // ошибка справочника не должна сбрасывать данные формы
    private async fetchReasons(): Promise<IApp21Reason[]> {
        try {
            const resp = await fetch('/api-py/app-21/dict-reasons-apps', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // по форме определяется дата актуальности причин — 31.12 отчётного года
                body: JSON.stringify({ form_id: this.currForm.id }),
            });
            if (!resp.ok) {
                throw new Error(`${resp.status} ${resp.statusText}`);
            }
            return await resp.json();
        } catch (error) {
            return [];
        }
    }


    // наименование БП по текущему языку; 
    private get prgName(): string {
        return (this.$i18n.locale === 'kk' ? this.prgNameKk || this.prgNameRu : this.prgNameRu) || '';
    }

    // все причины app21 — для показа причины, уже выбранной в строке (в т.ч. переставшей действовать)
    private get allReasonOptions(): IReasonOption[] {
        const locale = this.$i18n.locale;
        return this.reasonsDict.map(r => ({
            value: (locale === 'kk' ? r.name_kk : locale === 'en' ? r.name_en : r.name_ru) || r.name_ru,
            key: r.id,
        }));
    }

    // список для выбора — только причины, действующие на 31.12 отчётного года
    private get reasonOptions(): IReasonOption[] {
        const activeIds = new Set(this.reasonsDict.filter(r => r.is_active).map(r => r.id));
        return this.allReasonOptions.filter(o => activeIds.has(o.key));
    }

    // баннер о конечных результатах — на «Расходы по БП», о показателях мероприятий — на «Показатели» (п. 2.2-2.4)
    private get showKrBanner(): boolean {
        return this.view === 'expenses' && this.hasKrChanges && !this.krBannerClosed;
    }

    private get showPprBanner(): boolean {
        return this.view === 'indicators' && this.hasPprChanges && !this.pprBannerClosed;
    }

    // расхождений нет, закрытые баннеры сброшены — после «Обновить данные БП» и при смене формы
    private resetBpBanners() {
        this.hasKrChanges = false;
        this.hasPprChanges = false;
        this.krBannerClosed = false;
        this.pprBannerClosed = false;
    }

    // АС5: есть ранее сохранённые показатели БП — хотя бы одна активная строка КР или ППР
    // (удалённые is_deleted бэк не отдаёт); выбирает текст баннера NTF.04 / NTF.05
    private get hasSavedBpData(): boolean {
        return this.kr.length > 0 || this.indicators.length > 0;
    }

    // есть ли у БП подпрограммы (строки с ppr ≠ null)
    private get hasSubprograms(): boolean {
        return this.expenses.some(r => r.ppr !== null);
    }


    // причина строки budget_execution_application21 (раздел 1 обеих вкладок).
    // У БП без подпрограмм строка ppr = null общая для обеих вкладок: если на другой вкладке
    // уже была выбрана другая причина — сообщаем, что она заменена
    private onExpenseReasonChange(row: IApp21ExpenseRow, reasonId: number | null) {
        const prevId = row.reason_id;
        row.reason_id = reasonId;
        this.unhighlightIfFixed('expenses', row, MONEY_PRECISION);
        if (row.ppr === null && !this.hasSubprograms && prevId !== null && prevId !== reasonId) {
            const otherView: App21View = this.view === 'expenses' ? 'indicators' : 'expenses';
            const tab = this.$t(`${I18N_PREFIX}.view_${otherView}`) as string;
            this.$bvToast.toast(
                this.$t(`${I18N_PREFIX}.reason_synced`, { tab }) as string,
                { title: this.$t('modules.common.info') as string, variant: 'info', toaster: 'b-toaster-top-center', autoHideDelay: 5000 }
            );
        }
    }

    // факт КР или ППР; null — не введён. Выбранная причина при очистке факта сохраняется:
    // при ненулевом плане она по-прежнему обязательна
    private onFactChange(row: IApp21KrRow | IApp21PprIndicatorRow, fact: number | null) {
        row.fact = fact;
        this.unhighlightIfFixed('kr_code' in row ? 'kr' : 'ppr', row, INDICATOR_PRECISION);
    }

    // причина КР или ППР
    private onReasonChange(row: IApp21KrRow | IApp21PprIndicatorRow, reasonId: number | null) {
        row.reason_id = reasonId;
        this.unhighlightIfFixed('kr_code' in row ? 'kr' : 'ppr', row, INDICATOR_PRECISION);
    }

    // ошибка в строке исправлена — строка больше не подсвечивается до следующего «Сохранить»,
    // даже если отклонение без причины появится снова
    private unhighlightIfFixed(
        table: App21Table,
        row: { id: number; plan: number | null; fact: number | null; reason_id: number | null },
        precision: number,
    ) {
        const key = reasonKey(table, row.id);
        if (this.highlighted[key] && !isReasonMissing(row.plan, row.fact, row.reason_id, precision)) {
            this.$delete(this.highlighted, key);
        }
    }


    // выбранная подпрограмма; null — список пуст
    private get selectedPpr(): IApp21PprItem | null {
        return this.pprList[this.selectedPprIdx] ?? null;
    }

    private get hasPrevPpr(): boolean {
        return this.selectedPprIdx > 0;
    }

    private get hasNextPpr(): boolean {
        return this.selectedPprIdx < this.pprList.length - 1;
    }

    // «АБП.БП.БПП – Наименование»; БП без подпрограмм — «ХХХ.ХХХ.000 – Без подпрограммы»
    private get pprOptions(): IPprOption[] {
        const prefix = `${pad3(this.currForm?.abp)}.${pad3(this.currForm?.prg)}`;
        return this.pprList.map((item, idx) => {
            const name = item.ppr === null
                ? this.$t(`${I18N_PREFIX}.without_ppr`) as string
                : (this.$i18n.locale === 'kk' ? item.name_kk || item.name_ru : item.name_ru);
            const code = `${prefix}.${pad3(item.ppr)}`;
            return { value: name ? `${code} – ${name}` : code, key: idx };
        });
    }

    private get selectedPprOption(): IPprOption | null {
        return this.pprOptions[this.selectedPprIdx] ?? null;
    }

    private onPprInput(option: IPprOption | null) {
        if (option) {
            this.selectPprByIdx(option.key);
        }
    }

    // стрелки и выбор из списка
    // Несохранённые изменения сохраняются автоматически («Расходы по БП» и текущая подпрограмма) с той же проверкой, что «Сохранить»; 
    // при ошибке остаёмся на текущей подпрограмме
    // После «Обновить данные БП» автосохранения нет: правки остаются в форме до «Сохранить» или выхода (п. 1.6)
    private async selectPprByIdx(idx: number) {
        if (idx < 0 || idx >= this.pprList.length || idx === this.selectedPprIdx || this.saving) return;
        if (!this.bpPreview && this.isDirty && !(await this.persistData())) return;
        this.selectedPprIdx = idx;
    }

    
    private get viewOptions(): { value: App21View; text: string }[] {
        return [
            { 
                value: 'expenses',   
                text: this.$t('modules.budget_execution_monitoring.reports.app21.view_expenses') as string 
            },
            { 
                value: 'indicators', 
                text: this.$t('modules.budget_execution_monitoring.reports.app21.view_indicators') as string 
            },
        ];
    }
}
